import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'

import { adaptCriteriaToDto, getBlockingCriteria } from '../adapters/segment-criteria.adapter'
import type { SegmentCriteriaDto } from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import type { FilterCriterion } from '../types/filter-criteria.types'
import { debounce } from '../utils'

export const CRITERIA_PREVIEW_DEBOUNCE_MS = 500

export type UseSegmentCriteriaReturn = {
  /** What to hand `SegmentPreview`. Null while nothing is complete enough to evaluate. */
  criteriaDto: Ref<SegmentCriteriaDto | null>
  /** Rows that stop a save: unfinished ones, and ones that contradict another row. */
  blockingCriteria: ComputedRef<FilterCriterion[]>
}

export const useSegmentCriteria = (
  criteria: Ref<FilterCriterion[]>,
  { debounceMs = CRITERIA_PREVIEW_DEBOUNCE_MS }: { debounceMs?: number } = {},
): UseSegmentCriteriaReturn => {
  const criteriaDto = ref<SegmentCriteriaDto | null>(adaptCriteriaToDto(criteria.value))
  let lastSerialised = JSON.stringify(criteriaDto.value)

  const publish = () => {
    const next = adaptCriteriaToDto(criteria.value)
    const serialised = JSON.stringify(next)

    if (serialised === lastSerialised) {
      return
    }

    lastSerialised = serialised
    criteriaDto.value = next
  }

  const publishSoon = debounce(publish, debounceMs)

  watch(criteria, () => publishSoon(), { deep: true })

  const blockingCriteria = computed(() => getBlockingCriteria(criteria.value))

  return { criteriaDto, blockingCriteria }
}
